// PublishStepsPanel - 公開（完成）準備パネル（spec.md §5.8.3）
// サイドメニュー型: 右辺に常駐し、上端ヘッダーの chevron で collapse/expand。
// collapsed では正方形アイコンの縦並びだけ、expanded で進捗リング・ラベル・
// アクションボタン・フッターを全表示。アイコンは Material Symbols。
// 常時表示（モデル選択でも消えない）。モデルを配置したら自動で collapse する。

import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useState, useEffect, useLayoutEffect } from "react";
import axios from "axios";
import ShareIcon from "@mui/icons-material/Share";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { RootState } from "app/store";
import { USER_RESTRICTED_CODE, UserRestrictionFeature } from "app/types";
import { useUserRestriction, restrictionUntilText } from "app/restriction";
import I18n from "common/i18n";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { AccountType } from "features/user/types";
import { HELP_PUBLISH } from "features/builder/helpPages";
import { useBuilderScene } from "features/builder/context";
import { findBuilderAsset } from "features/builder/assets";
import { captureBuilderThumbnail } from "features/builder/captureThumbnail";
import { completePiece, publishPiece, updatePieceMeta } from "features/builder/api";
import { CompletionStepId, PieceStatus, derivePieceStatus } from "features/builder/types";
import {
  actions,
  selectAllMapModel,
  selectCompletionSteps,
  selectHasGround,
  selectCompletionCount,
  selectCanComplete,
} from "features/builder/slice";
import {
  PiecePublishOptions,
  BuilderModalAccent,
  BuilderConfirmDialog,
  BuilderInfoDialog,
  BuilderAccountRequiredDialog,
} from "features/builder/elements";
import { BuilderHelpPopup } from "./BuilderHelpPopup";
import { CommandSettingsModal } from "./CommandSettingsModal";
import { BuilderGuideTargetId } from "./BuilderTutorialOverlay";

const PANEL_HEIGHT = 604;
const DESC_MIN = 10;
const PANEL_TOP = 152;
const PANEL_WIDTH_COLLAPSED = 80;
const PANEL_WIDTH_EXPANDED = 576;
const ICON_BOX_SIZE = 56;
const RING_CIRCUMFERENCE = 176;

const STEP_CONFIG: {
  id: CompletionStepId;
  symbol: string;
  labelKey: string;
  btnKey: string;
  /** ボタンのアイコン。ビルダーのボタンはアイコン＋ラベルでそろえる */
  btnIcon: string;
}[] = [
  {
    id: CompletionStepId.CHARACTER,
    symbol: "person",
    labelKey: "MSG_BUILDER_STEP_CHARACTER",
    btnKey: "MSG_BUILDER_STEP_BTN_PLACE",
    btnIcon: "add_circle",
  },
  {
    id: CompletionStepId.GOAL,
    symbol: "flag",
    labelKey: "MSG_BUILDER_STEP_GOAL",
    btnKey: "MSG_BUILDER_STEP_BTN_PLACE",
    btnIcon: "add_circle",
  },
  {
    id: CompletionStepId.COMMANDS,
    symbol: "extension",
    labelKey: "MSG_BUILDER_STEP_COMMANDS",
    btnKey: "MSG_BUILDER_STEP_BTN_SETTING",
    btnIcon: "tune",
  },
  {
    id: CompletionStepId.TESTED,
    symbol: "play_arrow",
    labelKey: "MSG_BUILDER_STEP_TEST",
    btnKey: "MSG_BUILDER_STEP_BTN_RETRY",
    btnIcon: "replay",
  },
  {
    id: CompletionStepId.META,
    symbol: "description",
    labelKey: "MSG_BUILDER_STEP_META",
    btnKey: "MSG_BUILDER_STEP_BTN_EDIT",
    btnIcon: "edit",
  },
];

/** じめんが無いあいだ、キャラクターとゴールは置けない */
const needsGround = (id: CompletionStepId, hasGround: boolean) =>
  !hasGround && (id === CompletionStepId.CHARACTER || id === CompletionStepId.GOAL);

/** じめんが無いときに「おく」から選ばせるブロック（アセットバーのみどりのじめん） */
const GROUND_ASSET = "tileMedium_forest";

export const PublishStepsPanel = ({
  onTest,
  onEditInfo,
}: {
  onTest?: () => void;
  onEditInfo?: () => void;
}) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(false);
  const [showCommandModal, setShowCommandModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showPublishDenied, setShowPublishDenied] = useState(false);
  const [showPublishRestricted, setShowPublishRestricted] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  // 公開するときに作者が決める、このピースの許可（DBの既定はどちらも許可）
  const [publishOptions, setPublishOptions] = useState({ copyable: true, commentable: true });

  // パネル本体の実高さを測定し、外側コンテナの確保高（中身の自然高 × scale）を駆動する。
  // transform: scale はレイアウト高さに反映されないため、固定値ではなく実測値を使う。
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState(PANEL_HEIGHT);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const update = () => setBodyHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const steps = useSelector(selectCompletionSteps);
  const hasGround = useSelector(selectHasGround);
  const completionCount = useSelector(selectCompletionCount);
  const canComplete = useSelector(selectCanComplete);
  const enabledCommands = useSelector((state: RootState) => state.builder.enabledCommands);
  const description = useSelector((state: RootState) => state.builder.description);
  const status = useSelector((state: RootState) => state.builder.status);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const active = useSelector((state: RootState) => state.user.active);
  const isGuest = active?.account_type === AccountType.GUEST;
  // 保護者設定。OFF のときは公開できないので、押した時点で伝える
  const piecePublishAllowed = useSelector(
    (state: RootState) => state.config.userConfig.piece_publish,
  );
  // 運営が公開を止めている。保護者の許可を変えても直らないので、別の案内を出す
  const restriction = useUserRestriction(UserRestrictionFeature.PIECE_PUBLISH);
  const scene = useBuilderScene();

  // モデルを配置（数が増加）したら自動で折りたたむ
  const mapModelCount = useSelector(selectAllMapModel).length;
  const prevModelCountRef = useRef(mapModelCount);
  useEffect(() => {
    if (mapModelCount > prevModelCountRef.current) {
      setCollapsed(true);
    }
    prevModelCountRef.current = mapModelCount;
  }, [mapModelCount]);

  const isCompleted = status === PieceStatus.PUBLISHED || status === PieceStatus.UNPUBLISHED;

  const handleToggle = () => {
    play();
    btnDelay(() => setCollapsed((v) => !v));
  };

  const handleExpand = () => {
    play();
    btnDelay(() => setCollapsed(false));
  };

  const handleHelp = () => {
    play();
    btnDelay(() => setShowHelp(true));
  };

  // アセットバーのブロックを選んだ状態にして、そのままマップに置けるようにする
  const selectAsset = (name: string) => {
    const asset = findBuilderAsset(name);
    if (!asset) return;
    dispatch(actions.selectedAssetMesh(asset));
    dispatch(actions.updateActionMenu({ key: "asset", value: true, exclusive: true }));
    setCollapsed(true);
  };

  const handleStepAction = (id: CompletionStepId) => () => {
    play();
    btnDelay(() => {
      switch (id) {
        case CompletionStepId.COMMANDS:
          setShowCommandModal(true);
          break;
        case CompletionStepId.TESTED:
          onTest?.();
          break;
        case CompletionStepId.META:
          onEditInfo?.();
          break;
        // 「おく」を押したらアセットバーのキャラ/ゴールを選択状態にして、
        // そのままマップをタップ（またはドラッグ）で置けるようにする
        case CompletionStepId.CHARACTER:
        case CompletionStepId.GOAL: {
          // じめんが無いうちはキャラクターもゴールも置けない。押しても無反応だと
          // 子どもは次に何をすればいいか分からないので、代わりにじめんを選んで渡す
          if (!hasGround) {
            selectAsset(GROUND_ASSET);
            // 案内は、いま選んだ じめんブロックの真上に出す。
            // 画面いちばん上のトーストは、下を見ている子どもには気づけない
            const ground = [...document.querySelectorAll("img")].find((img) =>
              img.src.includes(GROUND_ASSET),
            );
            const anchor = (
              ground ?? document.getElementById(BuilderGuideTargetId.ASSET_SLOTS)
            )?.getBoundingClientRect();
            dispatch(
              actions.showPlaceHint({
                messageKey: "MSG_BUILDER_STEP_PLACE_GROUND_FIRST",
                x: anchor ? anchor.left + anchor.width / 2 : window.innerWidth / 2,
                y: anchor ? anchor.top : window.innerHeight / 2,
              }),
            );
            break;
          }
          selectAsset(id === CompletionStepId.CHARACTER ? "character_duck" : "flag_teamRed");
          break;
        }
        default:
          toast(
            <div>
              <p className="text text-textcolor-black">
                {I18n.t(STEP_CONFIG.find((s) => s.id === id)?.labelKey ?? "")}
              </p>
            </div>,
          );
          break;
      }
    });
  };

  const handleComplete = () => {
    if (!canComplete) {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_COMPLETE_FAILED")}</p>
        </div>,
      );
      return;
    }
    play();
    btnDelay(async () => {
      // 完成すると draft が消化され、以降の capture 保存は API に 409 で弾かれる。
      // そのため 3D マップのスクリーンショット（サムネイル）は完成の前に撮って保存する
      await captureBuilderThumbnail(scene, active.uid, pieceId).catch(() => undefined);
      const result = await completePiece({
        piece_id: pieceId,
        uid: active.uid,
        tested: true,
      });
      // API は status を返さない。3軸フラグから導出（完成後は draft 消化済み）
      dispatch(
        actions.setPieceStatus(
          derivePieceStatus({
            completed: result.completed,
            published: result.published,
            has_draft: false,
          }),
        ),
      );
      dispatch(actions.setDraftDirty(false));
      toast.success(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_COMPLETE_TOAST")}</p>
        </div>,
      );
    });
  };

  const handlePublish = () => {
    play();
    btnDelay(() => {
      // 公開できるかは手元の設定で分かる。API を投げて 403 を見るまでもなく、
      // 押した時点で理由を出す（運営が止めている / 未登録 / 保護者が公開を許可していない）
      if (!isGuest && restriction) {
        setShowPublishRestricted(true);
        return;
      }
      if (isGuest || !piecePublishAllowed) {
        setShowPublishDenied(true);
        return;
      }
      setShowPublishConfirm(true);
    });
  };

  const handleConfirmPublish = () => {
    setShowPublishConfirm(false);
    btnDelay(async () => {
      let result: { published: boolean };
      try {
        // 公開と同時に、このピースの許可（コピー/コメント）も保存する
        await updatePieceMeta({
          piece_id: pieceId,
          uid: active.uid,
          enable_copied: publishOptions.copyable,
          enable_comment: publishOptions.commentable,
        });
        result = await publishPiece({ piece_id: pieceId, uid: active.uid });
      } catch (error) {
        // 保護者が「ピースの こうかい」を許可していないと API が 403 を返す。
        // 子どもには何が起きたか分からないので、おうちのひとに相談する案内を出す
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          // 運営が止めている（手元の設定を読んだあとにかけられた等）
          if (error.response.data?.code === USER_RESTRICTED_CODE) {
            setShowPublishRestricted(true);
            return;
          }
          setShowPublishDenied(true);
          return;
        }
        throw error;
      }
      // 公開成功は completed=true が前提。フラグから導出
      dispatch(
        actions.setPieceStatus(
          derivePieceStatus({
            completed: true,
            published: result.published,
            has_draft: false,
          }),
        ),
      );
    });
  };

  const ringOffset = RING_CIRCUMFERENCE * (1 - completionCount / 5);

  return (
    <>
      <div
        className="flex-col-el absolute left-0 z-[9000] pointer-events-none"
        style={{ top: PANEL_TOP * scale }}
      >
        <div
          className="pointer-events-auto"
          style={{
            width: PANEL_WIDTH_EXPANDED * scale,
            height: bodyHeight * scale,
            marginLeft: 8 * scale,
          }}
        >
          {/* スケール層 */}
          <div
            className="flex-row-el items-start justify-start origin-top-left"
            style={{
              width: PANEL_WIDTH_EXPANDED,
              height: bodyHeight,
              transform: `scale(${scale})`,
            }}
          >
            {/* パネル本体（width をアニメーション、高さは collapsed/expanded で一致） */}
            <div
              ref={bodyRef}
              className="flex-col-el bg-white rounded-2xl overflow-hidden transition-[width] duration-300 ease-out"
              style={{
                width: collapsed ? PANEL_WIDTH_COLLAPSED : PANEL_WIDTH_EXPANDED,
              }}
            >
              {/* ヘッダー */}
              {collapsed ? (
                <div
                  id={BuilderGuideTargetId.PUBLISH_PROGRESS}
                  className="flex-row-el items-center justify-center p-4"
                >
                  <div className="flex-col-el flex-center h-14">
                    <button
                      onClick={handleToggle}
                      aria-label={I18n.t("MSG_BUILDER_PUBLISH_PANEL_TITLE")}
                      className="flex-col-el flex-center w-14 h-14 rounded-xl bg-blue-100 border-0 type-inherit-font shadow-md active:scale-95 transition-transform"
                    >
                      <span className="flex-col-el flex-center type-title text-white">
                        <ChevronRightIcon fontSize="inherit" />
                      </span>
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  id={BuilderGuideTargetId.PUBLISH_PROGRESS}
                  className="flex-row-el items-center gap-3 p-4"
                >
                  <div className="relative w-14 h-14 shrink-0">
                    <svg className="absolute inset-0" viewBox="0 0 64 64">
                      <circle cx="32" cy="32" r="28" fill="none" stroke="#E5E7EB" strokeWidth="6" />
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke="#2bdc98"
                        strokeWidth="6"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={ringOffset}
                        transform="rotate(-90 32 32)"
                      />
                    </svg>
                    <div className="flex-col-el flex-center absolute inset-0">
                      <span className="type-body text-green-100">{completionCount}/5</span>
                    </div>
                  </div>
                  <div className="flex-col-el items-start flex-1 min-w-0">
                    <span className="type-md text-textcolor-black whitespace-nowrap">
                      {I18n.t("MSG_BUILDER_PUBLISH_PANEL_TITLE")}
                    </span>
                    <span className="type-body text-gray-100 whitespace-nowrap">
                      {I18n.t("MSG_BUILDER_PUBLISH_PROGRESS_DONE")}
                    </span>
                  </div>
                  <button
                    onClick={handleHelp}
                    aria-label={I18n.t("MSG_BUILDER_HELP_BTN")}
                    className="flex-col-el flex-center shrink-0 w-14 h-14 rounded-xl bg-blue-10 border-2 border-blue-60 box-border type-inherit-font shadow-md active:scale-95 transition-transform"
                  >
                    <span className="flex-col-el flex-center type-title text-blue-100">
                      <HelpOutlineIcon fontSize="inherit" />
                    </span>
                  </button>
                  <button
                    onClick={handleToggle}
                    aria-label={I18n.t("MSG_BUILDER_PUBLISH_PANEL_TITLE")}
                    className="flex-col-el flex-center shrink-0 w-14 h-14 rounded-xl bg-blue-100 border-0 type-inherit-font shadow-md active:scale-95 transition-transform"
                  >
                    <span className="flex-col-el flex-center type-title text-white">
                      <ChevronLeftIcon fontSize="inherit" />
                    </span>
                  </button>
                </div>
              )}

              {/* コンテンツ領域（展開時のレイアウトは保持。閉じた時のみ box を絶対配置で
                  中央に重ね、展開時の各要素は invisible にして行の高さ・縦位置を一致させる） */}
              <div
                className={`flex-col-el items-stretch gap-2 ${
                  collapsed ? "overflow-hidden px-2" : "overflow-y-auto kid-scrollbar px-4"
                }`}
              >
                {STEP_CONFIG.map((step) => {
                  const done = steps[step.id];
                  return (
                    <div
                      key={step.id}
                      className={`relative flex-row-el items-center gap-2 rounded-xl p-2 ${
                        done ? "bg-green-100/10" : "bg-gray-10"
                      }`}
                    >
                      {collapsed && (
                        <button
                          onClick={handleExpand}
                          aria-label={I18n.t(step.labelKey)}
                          className={`flex-col-el flex-center absolute left-1/2 -translate-x-1/2 rounded-xl border-0 type-inherit-font ${
                            done ? "bg-green-100" : "bg-gray-40"
                          }`}
                          style={{ width: ICON_BOX_SIZE, height: ICON_BOX_SIZE }}
                        >
                          <span className="material-symbols-outlined type-hero text-white">
                            {step.symbol}
                          </span>
                          {done && (
                            <span className="flex-col-el flex-center absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white type-body text-green-100">
                              ✓
                            </span>
                          )}
                        </button>
                      )}
                      <span
                        className={`flex-col-el flex-center shrink-0 type-title ${
                          done ? "text-green-100" : "text-gray-30"
                        } ${collapsed ? "invisible" : ""}`}
                      >
                        {done ? (
                          <CheckCircleIcon fontSize="inherit" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="inherit" />
                        )}
                      </span>
                      <span
                        className={`material-symbols-outlined shrink-0 type-title text-gray-100 ${
                          collapsed ? "invisible" : ""
                        }`}
                      >
                        {step.symbol}
                      </span>
                      <div
                        className={`flex-col-el items-start flex-1 min-w-0 ${
                          collapsed ? "invisible" : ""
                        }`}
                      >
                        <span className="type-body text-textcolor-black whitespace-nowrap">
                          {I18n.t(step.labelKey)}
                        </span>
                        {/* 畳むと幅 80px になる。折り返すと行が伸びてアイコン列が崩れるので nowrap */}
                        {needsGround(step.id, hasGround) && (
                          <span className="type-sm text-danger whitespace-nowrap">
                            {I18n.t("MSG_BUILDER_STEP_NEED_GROUND")}
                          </span>
                        )}
                        {step.id === CompletionStepId.COMMANDS && (
                          <span className="type-body text-gray-100 whitespace-nowrap">
                            {I18n.t("MSG_BUILDER_STEP_COMMANDS_COUNT", {
                              count: enabledCommands.length,
                            })}
                          </span>
                        )}
                        {step.id === CompletionStepId.META &&
                          description.trim().length < DESC_MIN && (
                            <span className="type-body text-gray-100 whitespace-nowrap">
                              {I18n.t("MSG_BUILDER_META_DESC_MIN", {
                                count: DESC_MIN - description.trim().length,
                              })}
                            </span>
                          )}
                      </div>
                      <div className={`upsilon shrink-0 ${collapsed ? "invisible" : ""}`}>
                        <button
                          onClick={handleStepAction(step.id)}
                          className="btn btn-primary type-inherit-font !py-1 !px-3 w-48 box-border"
                        >
                          <div className="flex-row-center gap-1">
                            <span className="material-symbols-outlined text-white type-md">
                              {step.btnIcon}
                            </span>
                            <p className="type-text type-md text-white whitespace-nowrap">
                              {I18n.t(step.btnKey)}
                            </p>
                          </div>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* フッター（展開時のボタンを保持。閉じた時のみ box を絶対配置で重ね、
                  展開ボタンは invisible にして縦位置・高さを展開時と一致させる） */}
              <div
                className={`flex-col-el items-stretch gap-2 ${
                  collapsed ? "px-2 py-4" : "px-4 py-2"
                }`}
              >
                {isCompleted ? (
                  <div className={`upsilon ${collapsed ? "relative" : ""}`}>
                    {collapsed && (
                      <div className="upsilon-square flex-col-el flex-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <button
                          onClick={handlePublish}
                          aria-label={I18n.t("MSG_BUILDER_PUBLISH_SHARE_BTN")}
                          className="!flex flex-col-el flex-center btn btn-primary type-inherit-font !m-0 box-border"
                          style={{ width: ICON_BOX_SIZE, height: ICON_BOX_SIZE }}
                        >
                          <span className="material-symbols-outlined type-hero">share</span>
                        </button>
                      </div>
                    )}
                    <button
                      onClick={handlePublish}
                      className={`btn btn-primary type-inherit-font type-md w-full !m-0 whitespace-nowrap ${
                        collapsed ? "invisible" : ""
                      }`}
                    >
                      <span className="flex-row-el flex-center gap-2">
                        <span className="flex-col-el flex-center type-title">
                          <ShareIcon fontSize="inherit" />
                        </span>
                        {I18n.t("MSG_BUILDER_PUBLISH_SHARE_BTN")}
                      </span>
                    </button>
                  </div>
                ) : (
                  <div className={`upsilon ${collapsed ? "relative" : ""}`}>
                    {collapsed && (
                      <div className="upsilon-square flex-col-el flex-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <button
                          onClick={handleComplete}
                          disabled={!canComplete}
                          aria-label={I18n.t("MSG_BUILDER_COMPLETE_BTN")}
                          className="!flex flex-col-el flex-center btn btn-primary type-inherit-font !m-0 box-border"
                          style={{ width: ICON_BOX_SIZE, height: ICON_BOX_SIZE }}
                        >
                          <span className="material-symbols-outlined type-hero">check_circle</span>
                        </button>
                      </div>
                    )}
                    <button
                      onClick={handleComplete}
                      disabled={!canComplete}
                      className={`btn btn-primary type-inherit-font type-md w-full !m-0 whitespace-nowrap ${
                        collapsed ? "invisible" : ""
                      }`}
                    >
                      <div className="flex-row-center gap-1">
                        <span className="material-symbols-outlined text-white type-md">
                          {canComplete ? "rocket_launch" : "lock"}
                        </span>
                        <p className="type-text type-md text-white whitespace-nowrap">
                          {I18n.t(
                            canComplete ? "MSG_BUILDER_COMPLETE_BTN" : "MSG_BUILDER_INCOMPLETE_BTN",
                          )}
                        </p>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CommandSettingsModal open={showCommandModal} onClose={() => setShowCommandModal(false)} />

      <BuilderHelpPopup
        open={showHelp}
        onClose={() => setShowHelp(false)}
        chapters={HELP_PUBLISH}
      />

      {/* 保護者が「ピースの こうかい」を許可していない。
          閉じるだけでは直せないので、みまもりせってい へ進める導線を出す */}
      {/* 公開の確認。ここで このピースの許可（コピー/コメント）も決める */}
      <BuilderConfirmDialog
        visible={showPublishConfirm}
        accent={BuilderModalAccent.GREEN}
        icon="public"
        confirmIcon="public"
        confirmLabel={I18n.t("MSG_CONFIRM_BTN")}
        title={I18n.t("MSG_BUILDER_PUBLISH_SHARE_BTN")}
        message={I18n.t("MSG_BUILDER_PUBLISH_HINT")}
        close={() => setShowPublishConfirm(false)}
        handleConfirm={handleConfirmPublish}
        extra={
          <PiecePublishOptions
            copyable={publishOptions.copyable}
            commentable={publishOptions.commentable}
            onChange={setPublishOptions}
          />
        }
      />

      <BuilderAccountRequiredDialog
        visible={showPublishDenied}
        title={I18n.t("MSG_BUILDER_PUBLISH_DENIED_TITLE")}
        message={I18n.t("MSG_BUILDER_PUBLISH_DENIED_MESSAGE")}
        close={() => setShowPublishDenied(false)}
      />

      {/* 運営が公開を止めている。保護者ページでは直せないので、閉じるだけにする */}
      <BuilderInfoDialog
        visible={showPublishRestricted}
        accent={BuilderModalAccent.GREEN}
        icon="public_off"
        title={I18n.t("MSG_USER_RESTRICTION_PIECE_PUBLISH_TITLE")}
        message={I18n.t("MSG_USER_RESTRICTION_PIECE_PUBLISH_MESSAGE", {
          until: restrictionUntilText(restriction),
        })}
        close={() => setShowPublishRestricted(false)}
      />
    </>
  );
};
